import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { IconPrefix } from '@fortawesome/pro-light-svg-icons';
import { EnvironmentService } from '@libs/shared/common';
import { AppError, ComponentsIconsStyle, IconDefinition, IconName } from '@libs/shared/models';
import { StatusCodes } from 'http-status-codes';

@Injectable({
  providedIn: 'root',
})
export class IconLoaderService {
  private readonly doc: Document = inject(DOCUMENT);
  private readonly env: EnvironmentService = inject(EnvironmentService);

  private readonly iconPrefixes: Record<ComponentsIconsStyle, IconPrefix> = {
    solid: 'fas',
    light: 'fal',
    brands: 'fab',
    custom: 'fak',
  };

  public async load(style: ComponentsIconsStyle, name: IconName): Promise<IconDefinition | undefined> {
    return this.loadSvgString(style, name)
      .then(svgData => this.createSvgElement(svgData))
      .then(svg => this.buildIconDefinition(style, name, svg))
      .catch(error => {
        console.error(`Failed to load icon ${name} ${style}`, error);

        return undefined;
      });
  }

  private buildIconDefinition(style: ComponentsIconsStyle, name: IconName, svg: SVGSVGElement): IconDefinition {
    // Note: svg.viewBox property is undefined in SSR mode
    const viewBox: string[] | undefined = svg.getAttribute('viewBox')?.split(' ') ?? [];
    const width: number = Number(viewBox[2] ?? 0);
    const height: number = Number(viewBox[3] ?? 0);

    const path: string = svg.querySelector('path')?.getAttribute('d') ?? '';

    return {
      iconName: name,
      prefix: this.iconPrefixes[style],
      icon: [width, height, [], '', path],
    };
  }

  private createSvgElement(svgData: string): SVGSVGElement {
    const template: HTMLTemplateElement = this.doc.createElement('template');

    template.innerHTML = svgData;

    const svg: SVGSVGElement | null = template.content.querySelector('svg');

    if (!svg) {
      throw AppError.create('Icon loader', StatusCodes.INTERNAL_SERVER_ERROR, 'SVG element not found');
    }

    return svg;
  }

  private async loadSvgString(style: ComponentsIconsStyle, name: IconName): Promise<string> {
    return fetch(this.getSvgUrl(style, name))
      .then(res => res.text());
  }

  private getSvgUrl(style: ComponentsIconsStyle, name: IconName): string {
    return `${this.env.app().baseHref}/assets/icons/${style}/${name}.svg`;
  }
}
